/**
 * Data hooks for §6 Opportunities & pipeline.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as oppApi from '../../api/opportunities.api.js';
import * as pipelineApi from '../../api/pipeline.api.js';
import * as masterDataApi from '../../api/masterData.api.js';
import * as accountsApi from '../../api/accounts.api.js';
import * as usersApi from '../../api/users.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

export function useOpportunities(params) {
  return useQuery({
    queryKey: queryKeys.opportunities.list(params),
    queryFn: () => oppApi.listOpportunities(params),
    placeholderData: (previous) => previous,
  });
}

export function useOpportunity(id) {
  return useQuery({
    queryKey: queryKeys.opportunities.detail(id),
    queryFn: () => oppApi.getOpportunity(id),
    enabled: Boolean(id),
  });
}

export function useOpportunityStageHistory(id, enabled = true) {
  return useQuery({
    queryKey: queryKeys.opportunities.stageHistory(id),
    queryFn: () => oppApi.getOpportunityStageHistory(id),
    enabled: Boolean(id) && enabled,
  });
}

export function usePipelineBoard(params) {
  return useQuery({
    queryKey: queryKeys.pipeline.board(params),
    queryFn: () => pipelineApi.getPipelineBoard(params),
    placeholderData: (previous) => previous,
  });
}

export function usePipelineVelocity(params) {
  return useQuery({
    queryKey: queryKeys.pipeline.velocity(params),
    queryFn: () => pipelineApi.getPipelineVelocity(params),
  });
}

export function useLostOpportunities(params) {
  return useQuery({
    queryKey: queryKeys.analytics.lostOpportunities(params),
    queryFn: () => pipelineApi.getLostOpportunities(params),
  });
}

export function useCreateOpportunity() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: (payload) => oppApi.createOpportunity(payload),
    onSuccess: (o) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunities.all });
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      dispatch(pushToast({ tone: 'success', message: `Opportunity "${o.name}" created.` }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not create that opportunity', detail: error.message })),
  });
}

export function useUpdateOpportunity() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => oppApi.updateOpportunity(id, patch, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunities.all });
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
    },
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useDeleteOpportunity() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => oppApi.deleteOpportunity(id, { reason, rowVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunities.all });
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      dispatch(pushToast({ tone: 'success', message: 'Opportunity deleted.' }));
    },
    onError: (error) => dispatch(pushToast({ tone: 'danger', message: 'Could not delete that opportunity', detail: error.message })),
  });
}

/**
 * Move a deal to another stage. Used by both the list's row menu and the
 * pipeline board's drag-and-drop — the mutation itself has no board-specific
 * optimistic-update logic; `PipelinePage` layers that on top via `onMutate`.
 */
export function useChangeStage() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: ({ id, payload }) => oppApi.changeOpportunityStage(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.opportunities.all });
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
    },
    onError: (error) => {
      const lostReasonRequired = error?.code === 'lost_reason_required';
      const closed = error?.code === 'opportunity_closed';
      dispatch(pushToast({
        tone: 'danger',
        message: lostReasonRequired ? 'A lost reason is required' : closed ? 'That deal is already closed' : 'Could not move that deal',
        detail: lostReasonRequired || closed ? undefined : error.message,
      }));
    },
  });
}

/** Dropdown options the create/edit form and pipeline filters need. */
export function useOpportunityOptions() {
  const pipelines = useQuery({
    queryKey: queryKeys.masterData.options('pipelines'),
    queryFn: () => masterDataApi.listRecords('pipelines', { limit: 50, is_active: 'true' }),
    staleTime: 300_000,
  });
  const stages = useQuery({
    queryKey: queryKeys.masterData.options('opportunity_stages'),
    queryFn: () => masterDataApi.listRecords('opportunity_stages', { limit: 200, is_active: 'true' }),
    staleTime: 300_000,
  });
  const lostReasons = useQuery({
    queryKey: queryKeys.masterData.options('lost_reasons'),
    queryFn: () => masterDataApi.listRecords('lost_reasons', { limit: 200, is_active: 'true' }),
    staleTime: 300_000,
  });
  const competitors = useQuery({
    queryKey: queryKeys.masterData.options('competitors'),
    queryFn: () => masterDataApi.listRecords('competitors', { limit: 200, is_active: 'true' }),
    staleTime: 300_000,
  });
  const accounts = useQuery({
    queryKey: queryKeys.accounts.list({ limit: 200, sort: 'name' }),
    queryFn: () => accountsApi.listAccounts({ limit: 200, sort: 'name' }),
    staleTime: 300_000,
  });
  const users = useQuery({
    queryKey: queryKeys.users.list({ limit: 200, picker: true }),
    queryFn: () => usersApi.listUsers({ limit: 200 }),
    staleTime: 300_000,
  });

  const pipelineRows = pipelines.data?.data ?? [];
  const stageRows = stages.data?.data ?? [];
  const lostReasonRows = lostReasons.data?.data ?? [];
  const competitorRows = competitors.data?.data ?? [];
  const accountRows = accounts.data?.data ?? [];
  const userRows = users.data?.data ?? [];
  const loading = [pipelines, stages, lostReasons, competitors, accounts, users].some((q) => q.isLoading);

  return useMemo(() => ({
    pipelines: pipelineRows.map((p) => ({ value: p.id, label: p.name, isDefault: p.is_default })),
    stages: stageRows.map((s) => ({
      value: s.id, label: s.name, pipelineId: s.pipeline_id, sortOrder: s.sort_order,
      probability: s.probability, forecastCategory: s.forecast_category, isClosed: s.is_closed, isWon: s.is_won,
      exitCriteria: s.exit_criteria,
    })),
    lostReasons: lostReasonRows.map((r) => ({ value: r.id, label: r.name })),
    competitors: competitorRows.map((c) => ({ value: c.id, label: c.name })),
    accounts: accountRows.map((a) => ({ value: a.id, label: a.name })),
    owners: userRows.map((u) => ({ value: u.id, label: u.full_name ?? u.email })),
    loading,
  }), [pipelineRows, stageRows, lostReasonRows, competitorRows, accountRows, userRows, loading]);
}
